/**
 * i-GlooBot Controller
 *
 * Manages state and gateway RPC calls for the i-GlooBot admin views
 * (Projects, Tasks, Users).
 */

import type {
  AvatarAdminProps,
  AvatarConfig,
  AvatarServiceStatus,
  AvatarSessionEntry,
} from "../views/avatar-admin.ts";
import type { CompanyEntry, CompanyProps } from "../views/company-admin.ts";
import type {
  EmailAgentOption,
  EmailConfigEntry,
  EmailProps,
  EmailTestResult,
  EmailServiceStatus,
} from "../views/email-admin.ts";
import type { ProjectEntry, ProjectsProps } from "../views/projects.ts";
import type {
  SkillEntry,
  SkillsAdminProps,
  SkillStatusFilter,
  SkillCategoryFilter,
} from "../views/skills-admin.ts";
import type { TaskEntry, TasksAdminProps } from "../views/tasks-admin.ts";
import type { UserEntry, UserProjectAssignment, UsersAdminProps } from "../views/users-admin.ts";
import { addPin, removePin } from "../pinned-projects.js";
import type { UiSettings } from "../storage.ts";
import { loadAgentDefinitions, type AgentDefinitionsState } from "./agent-definitions.ts";
import { loadAgents, type AgentsState } from "./agents.ts";
import { loadAllChannelConfigs, type ChannelConfigsState } from "./channel-configs.ts";

// --------------- Types ---------------

export type ProjectSessionEntry = {
  id: string;
  projectId: string;
  userId: string | null;
  type: "chat" | "voice" | "avatar";
  status: "active" | "paused" | "completed" | "failed";
  channel: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  projectName?: string | null;
  userName?: string | null;
};

export type ProjectUserEntry = {
  userId: string;
  projectId: string;
  role: "admin" | "project_admin" | "user";
  isActive: boolean;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  channels: Array<{ channel: string; channelUserId: string }>;
  preferredChannel: string | null;
};

export type CronJobEntry = {
  id: string;
  projectId: string;
  projectName?: string;
  name: string;
  cronExpression: string;
  timezone: string;
  /** Active-window start (ISO) — null/empty = active immediately. */
  startAt: string | null;
  /** Active-window end (ISO) — null/empty = never expires. */
  endAt: string | null;
  trigger: "create_task" | "send_message" | "run_skill";
  payload: Record<string, unknown>;
  isActive: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TemplateSummary = {
  id: string;
  name: string;
  description: string;
  templateType: string;
  coreInstruction: string;
  payload: Record<string, unknown>;
};

export type ProjectContactEntry = {
  id: string;
  projectId: string;
  name: string;
  channel: string;
  address: string;
  tags: string[];
  notes: string | null;
  metadata: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ProjectDetailPanel =
  | "overview"
  | "skills"
  | "cron"
  | "tasks"
  | "users"
  | "contacts"
  | "sessions"
  | "knowledge-base"
  | "workspace";

// --------------- State ---------------

export interface IgloobotState {
  client: { request<T>(method: string, params: unknown): Promise<T> } | null;
  connected: boolean;

  // Projects
  igProjects: ProjectEntry[];
  igProjectsLoading: boolean;
  igProjectsError: string | null;
  igSelectedProjectId: string | null;
  igEditMode: boolean;
  igEditForm: Partial<ProjectEntry> | null;

  // Tasks
  igTasks: TaskEntry[];
  igTasksLoading: boolean;
  igTasksError: string | null;
  igTaskStatusFilter: string | null;
  igTaskTypeFilter: string | null;
  igTaskProjectFilter: string | null;
  igTaskAgentFilter: string | null;
  igSelectedTaskId: string | null;
  igTaskCreateMode: boolean;
  igTaskCreateForm: Record<string, unknown> | null;

  // Users
  igUsers: UserEntry[];
  igUsersLoading: boolean;
  igUsersError: string | null;
  igUserSearchQuery: string;
  igSelectedUserId: string | null;
  igSelectedUserProjects: UserProjectAssignment[];
  igUserEditMode: boolean;
  igUserEditForm: Partial<UserEntry> | null;
  igUserCreateMode: boolean;
  igUserCreateForm: Partial<UserEntry> | null;
  igUserEmailCreds: import("../views/users-admin.ts").EmailCredEntry | null;
  igUserEmailCredsLoading: boolean;
  igUserEmailCredsSaving: boolean;
  igUserEmailCredsError: string | null;
  igUserEmailCredsTestResult: string | null;
  igUserEmailCredsForm: Partial<import("../views/users-admin.ts").EmailCredEntry> | null;

  // MCP Skills
  igMcpSkills: SkillEntry[];
  igMcpSkillsLoading: boolean;
  igMcpSkillsError: string | null;
  igMcpSkillsSearchQuery: string;
  igMcpSelectedSkillId: string | null;
  igMcpSkillEditMode: boolean;
  igMcpSkillCreateMode: boolean;
  igMcpSkillEditForm: Partial<SkillEntry> | null;
  igMcpSkillsStatusFilter: SkillStatusFilter;
  igMcpSkillsCategoryFilter: SkillCategoryFilter;
  igMcpSkillsSyncLoading: boolean;
  igMcpSkillsSyncResult: { imported: number; skipped: number } | null;

  // Avatar
  igAvatarStatus: AvatarServiceStatus | null;
  igAvatarConfig: AvatarConfig | null;
  igAvatarSessions: AvatarSessionEntry[];
  igAvatarLoading: boolean;
  igAvatarError: string | null;
  igAvatarConfigEditMode: boolean;
  igAvatarConfigForm: Partial<AvatarConfig> | null;
  igAvatarSelectedSessionId: string | null;
  igAvatarStatusFilter: string;

  // Project Agent Sessions
  igProjectSessions: ProjectSessionEntry[];
  igProjectSessionsLoading: boolean;

  // Project Detail Panel
  igProjectDetailPanel: ProjectDetailPanel;
  igProjectUsers: ProjectUserEntry[];
  igProjectUsersLoading: boolean;
  igProjectSkills: SkillEntry[];
  igProjectSkillsLoading: boolean;
  igProjectCronJobs: CronJobEntry[];
  igProjectCronJobsLoading: boolean;
  igProjectTasks: TaskEntry[];
  igProjectTasksLoading: boolean;
  igProjectContacts: ProjectContactEntry[];
  igProjectContactsLoading: boolean;
  igContactCreateMode: boolean;
  igContactEditingId: string | null;
  igCronCreateMode: boolean;
  igCronCreateForm: Partial<CronJobEntry> | null;
  igCronTemplates: TemplateSummary[];
  igTaskTemplates: TemplateSummary[];
  igCronEditingId: string | null;
  igCronEditForm: Partial<CronJobEntry> | null;
  igSessionCreateMode: boolean;

  // Global Cron tab (project cron for merged view)
  igGlobalCronJobs: CronJobEntry[];
  igGlobalCronJobsLoading: boolean;
  igGlobalCronProjectFilter: string | null;
  igGlobalCronAgentFilter: string | null;

  // Company (multi-company)
  igCompanies: CompanyEntry[];
  igCompaniesLoading: boolean;
  igActiveCompanyId: string | null;
  igCompany: CompanyEntry | null;
  igCompanyLoading: boolean;
  igCompanyError: string | null;
  igCompanySaving: boolean;
  igCompanyForm: Partial<CompanyEntry> | null;
  igCompanyVatNumbers: Array<{
    id: string;
    companyId: string;
    country: string;
    vatNumber: string;
    vatRate: number;
    isDefault: boolean;
  }>;

  // Email Config (per-agent)
  igEmailSelectedAgentId: string | null;
  igEmailAgents: EmailAgentOption[];
  igEmailConfig: EmailConfigEntry | null;
  igEmailLoading: boolean;
  igEmailError: string | null;
  igEmailSaving: boolean;
  igEmailForm: Partial<EmailConfigEntry> | null;
  igEmailTesting: boolean;
  igEmailTestAddress: string;
  igEmailTestResult: EmailTestResult | null;
  igEmailStatus: EmailServiceStatus | null;

  // Knowledge Base sync
  igKnowledgeBaseSyncing: boolean;
  igKnowledgeBaseSyncResult: {
    processed: number;
    skipped: number;
    failed: number;
    totalFiles: number;
    errors?: string[];
  } | null;

  // Workspace verification
  igWorkspaceVerifying: boolean;
  igWorkspaceVerifyResult: {
    exists: boolean;
    readable: boolean;
    writable: boolean;
    fileCount?: number;
    error?: string;
  } | null;

  // Agents (from AppViewState, accessed for project agent picker)
  agentsList: { agents: Array<{ id: string; name?: string; identity?: { name?: string } }> } | null;
}

export const IGLOOBOT_STATE_DEFAULTS: Omit<IgloobotState, "client" | "connected"> = {
  igProjects: [],
  igProjectsLoading: false,
  igProjectsError: null,
  igSelectedProjectId: null,
  igEditMode: false,
  igEditForm: null,
  igTasks: [],
  igTasksLoading: false,
  igTasksError: null,
  igTaskStatusFilter: null,
  igTaskTypeFilter: null,
  igTaskProjectFilter: null,
  igTaskAgentFilter: null,
  igSelectedTaskId: null,
  igTaskCreateMode: false,
  igTaskCreateForm: null,
  igUsers: [],
  igUsersLoading: false,
  igUsersError: null,
  igUserSearchQuery: "",
  igSelectedUserId: null,
  igSelectedUserProjects: [],
  igUserEditMode: false,
  igUserEditForm: null,
  igUserCreateMode: false,
    igUserCreateForm: null,
    igUserEmailCreds: null,
    igUserEmailCredsLoading: false,
    igUserEmailCredsSaving: false,
    igUserEmailCredsError: null,
    igUserEmailCredsTestResult: null,
    igUserEmailCredsForm: null,
  igMcpSkills: [],
  igMcpSkillsLoading: false,
  igMcpSkillsError: null,
  igMcpSkillsSearchQuery: "",
  igMcpSelectedSkillId: null,
  igMcpSkillEditMode: false,
  igMcpSkillCreateMode: false,
  igMcpSkillEditForm: null,
  igMcpSkillsStatusFilter: "all",
  igMcpSkillsCategoryFilter: "all",
  igMcpSkillsSyncLoading: false,
  igMcpSkillsSyncResult: null,
  igAvatarStatus: null,
  igAvatarConfig: null,
  igAvatarSessions: [],
  igAvatarLoading: false,
  igAvatarError: null,
  igAvatarConfigEditMode: false,
  igAvatarConfigForm: null,
  igAvatarSelectedSessionId: null,
  igAvatarStatusFilter: "",
  igProjectSessions: [],
  igProjectSessionsLoading: false,
  igProjectDetailPanel: "overview",
  igProjectUsers: [],
  igProjectUsersLoading: false,
  igProjectSkills: [],
  igProjectSkillsLoading: false,
  igProjectCronJobs: [],
  igProjectCronJobsLoading: false,
  igProjectTasks: [],
  igProjectTasksLoading: false,
  igProjectContacts: [],
  igProjectContactsLoading: false,
  igContactCreateMode: false,
  igContactEditingId: null,
  igCronCreateMode: false,
  igCronCreateForm: null,
  igCronTemplates: [],
  igTaskTemplates: [],
  igCronEditingId: null,
  igCronEditForm: null,
  igSessionCreateMode: false,
  igGlobalCronJobs: [],
  igGlobalCronJobsLoading: false,
  igGlobalCronProjectFilter: null,
  igGlobalCronAgentFilter: null,
  igCompanies: [],
  igCompaniesLoading: false,
  igActiveCompanyId: null,
  igCompany: null,
  igCompanyLoading: false,
  igCompanyError: null,
  igCompanySaving: false,
    igCompanyForm: null,
    igCompanyVatNumbers: [],
  igEmailSelectedAgentId: null,
  igEmailAgents: [],
  igEmailConfig: null,
  igEmailLoading: false,
  igEmailError: null,
  igEmailSaving: false,
  igEmailForm: null,
  igEmailTesting: false,
  igEmailTestAddress: "",
  igEmailTestResult: null,
  igEmailStatus: null,
  igKnowledgeBaseSyncing: false,
  igKnowledgeBaseSyncResult: null,
  igWorkspaceVerifying: false,
  igWorkspaceVerifyResult: null,
  agentsList: null,
};

// --------------- Helpers ---------------

/** Detect RPC "method not found" or DB connection errors and return a friendly message. */
function friendlyError(err: unknown): string {
  const msg = String(err).toLowerCase();
  if (msg.includes("method not found") || msg.includes("not registered")) {
    return "i-GlooBot plugin is not loaded. Ensure the igloobot-core extension is enabled and the database is configured.";
  }
  if (msg.includes("connection refused") || msg.includes("econnrefused")) {
    return "Database connection failed. Check that PostgreSQL is running and IGLOOBOT_DATABASE_URL is set.";
  }
  return String(err);
}

// --------------- Projects ---------------

type ProjectsListResult = { ok: boolean; projects?: ProjectEntry[]; error?: string };
type ProjectResult = { ok: boolean; project?: ProjectEntry; error?: string };
type SimpleResult = { ok: boolean; error?: string };

export async function loadProjects(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igProjectsLoading = true;
  state.igProjectsError = null;
  try {
    const res = await state.client.request<ProjectsListResult>("igloobot.projects.list", {});
    if (res.ok && res.projects) {
      state.igProjects = res.projects;
    } else {
      state.igProjectsError = res.error ?? "Failed to load projects";
    }
  } catch (err) {
    state.igProjectsError = friendlyError(err);
  } finally {
    state.igProjectsLoading = false;
  }
}

export function selectProject(state: IgloobotState, id: string): void {
  state.igSelectedProjectId = id;
  state.igEditMode = false;
  state.igEditForm = null;
  state.igProjectDetailPanel = "overview";
}

export function setProjectDetailPanel(state: IgloobotState, panel: ProjectDetailPanel): void {
  state.igProjectDetailPanel = panel;
  if (!state.igSelectedProjectId) return;
  const pid = state.igSelectedProjectId;
  if (panel === "users") void loadProjectUsers(state, pid);
  else if (panel === "contacts") void loadProjectContacts(state, pid);
  else if (panel === "skills") void loadProjectSkills(state, pid);
  else if (panel === "cron") {
    void loadProjectCronJobs(state, pid);
    void loadTemplates(state, "cron_job", pid);
  } else if (panel === "tasks") {
    void loadProjectTasks(state, pid);
    void loadTemplates(state, "task", pid);
  } else if (panel === "sessions") void loadProjectSessions(state, pid);
}

// --------------- Project Detail Loaders ---------------

type ProjectUsersResult = { ok: boolean; projectUsers?: ProjectUserEntry[]; error?: string };
type ProjectContactsResult = { ok: boolean; contacts?: ProjectContactEntry[]; error?: string };
type ContactResult = { ok: boolean; contact?: ProjectContactEntry; error?: string };
type CronJobsResult = { ok: boolean; cronJobs?: CronJobEntry[]; error?: string };
type CronJobResult = { ok: boolean; cronJob?: CronJobEntry; error?: string };
type SessionCreateResult = { ok: boolean; session?: ProjectSessionEntry; error?: string };

export async function loadProjectUsers(state: IgloobotState, projectId: string): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igProjectUsersLoading = true;
  try {
    const res = await state.client.request<ProjectUsersResult>("igloobot.projects.users", {
      projectId,
    });
    if (res.ok && res.projectUsers) {
      state.igProjectUsers = res.projectUsers;
    }
  } catch {
    state.igProjectUsers = [];
  } finally {
    state.igProjectUsersLoading = false;
  }
}

export async function loadProjectContacts(state: IgloobotState, projectId: string): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igProjectContactsLoading = true;
  try {
    const res = await state.client.request<ProjectContactsResult>("igloobot.contacts.list", {
      projectId,
      activeOnly: false,
    });
    if (res.ok && res.contacts) {
      state.igProjectContacts = res.contacts;
    }
  } catch {
    state.igProjectContacts = [];
  } finally {
    state.igProjectContactsLoading = false;
  }
}

export async function loadProjectSkills(state: IgloobotState, projectId: string): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igProjectSkillsLoading = true;
  try {
    const res = await state.client.request<SkillsListResult>("igloobot.skills.list", {
      projectId,
      activeOnly: false,
    });
    if (res.ok && res.skills) {
      state.igProjectSkills = res.skills;
    }
  } catch {
    state.igProjectSkills = [];
  } finally {
    state.igProjectSkillsLoading = false;
  }
}

// --------------- Agent Project Cron (read-only, for agent panel) ---------------

export interface AgentProjectCronState {
  client: { request<T>(method: string, params: unknown): Promise<T> } | null;
  connected: boolean;
  agentProjectCronJobs: CronJobEntry[];
  agentProjectCronJobsLoading: boolean;
}

export async function loadAgentProjectCronJobs(
  state: AgentProjectCronState,
  agentId: string,
): Promise<void> {
  if (!state.client || !state.connected) return;
  state.agentProjectCronJobsLoading = true;
  try {
    const res = await state.client.request<CronJobsResult>("igloobot.cron.list", { agentId });
    if (res.ok && res.cronJobs) {
      state.agentProjectCronJobs = res.cronJobs;
    } else {
      state.agentProjectCronJobs = [];
    }
  } catch {
    state.agentProjectCronJobs = [];
  } finally {
    state.agentProjectCronJobsLoading = false;
  }
}

export async function loadGlobalProjectCronJobs(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) return;
  void loadTemplates(state, "cron_job");
  state.igGlobalCronJobsLoading = true;
  try {
    const params: Record<string, string> = {};
    if (state.igGlobalCronProjectFilter) {
      params.projectId = state.igGlobalCronProjectFilter;
    }
    if (state.igGlobalCronAgentFilter) {
      params.agentId = state.igGlobalCronAgentFilter;
    }
    const res = await state.client.request<CronJobsResult>("igloobot.cron.list", params);
    if (res.ok && res.cronJobs) {
      state.igGlobalCronJobs = res.cronJobs;
    }
  } catch {
    state.igGlobalCronJobs = [];
  } finally {
    state.igGlobalCronJobsLoading = false;
  }
}

export async function loadProjectCronJobs(state: IgloobotState, projectId: string): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igProjectCronJobsLoading = true;
  try {
    const res = await state.client.request<CronJobsResult>("igloobot.cron.list", { projectId });
    if (res.ok && res.cronJobs) {
      state.igProjectCronJobs = res.cronJobs;
    }
  } catch {
    state.igProjectCronJobs = [];
  } finally {
    state.igProjectCronJobsLoading = false;
  }
}

export async function loadTemplates(
  state: IgloobotState,
  type: "cron_job" | "task" | "office_template",
  projectId?: string,
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    const res = await state.client.request<{ ok: boolean; templates?: TemplateSummary[] }>(
      "igloobot.templates.list",
      { type, projectId },
    );
    if (res.ok && res.templates) {
      if (type === "cron_job") {
        state.igCronTemplates = res.templates;
      } else if (type === "task") {
        state.igTaskTemplates = res.templates;
      }
    }
  } catch {
    if (type === "cron_job") {
      state.igCronTemplates = [];
    } else if (type === "task") {
      state.igTaskTemplates = [];
    }
  }
}

export async function loadProjectTasks(state: IgloobotState, projectId: string): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igProjectTasksLoading = true;
  try {
    const res = await state.client.request<TasksListResult>("igloobot.tasks.list", { projectId });
    if (res.ok && res.tasks) {
      state.igProjectTasks = res.tasks;
    }
  } catch {
    state.igProjectTasks = [];
  } finally {
    state.igProjectTasksLoading = false;
  }
}

// --------------- Project Cron Job CRUD ---------------

export function startCreateCronJob(state: IgloobotState): void {
  state.igCronCreateMode = true;
  state.igCronCreateForm = {
    name: "",
    cronExpression: "",
    timezone: "UTC",
    startAt: null,
    endAt: null,
    trigger: "create_task",
    payload: {},
  };
}

export function cancelCreateCronJob(state: IgloobotState): void {
  state.igCronCreateMode = false;
  state.igCronCreateForm = null;
}

export function updateCronCreateForm(state: IgloobotState, field: string, value: unknown): void {
  if (!state.igCronCreateForm) return;
  (state.igCronCreateForm as Record<string, unknown>)[field] = value;
  state.igCronCreateForm = { ...state.igCronCreateForm };
}

export async function saveNewCronJob(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igCronCreateForm || !state.igSelectedProjectId)
    return;
  const form = state.igCronCreateForm;
  if (!form.name || !form.cronExpression) {
    state.igProjectsError = "Name and cron expression are required";
    return;
  }
  try {
    await state.client.request<CronJobResult>("igloobot.cron.create", {
      projectId: state.igSelectedProjectId,
      name: form.name,
      cronExpression: form.cronExpression,
      timezone: form.timezone ?? "UTC",
      startAt: form.startAt ?? null,
      endAt: form.endAt ?? null,
      trigger: form.trigger ?? "create_task",
      payload: form.payload ?? {},
    });
    state.igCronCreateMode = false;
    state.igCronCreateForm = null;
    await loadProjectCronJobs(state, state.igSelectedProjectId);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function globalToggleCronJobActive(
  state: IgloobotState,
  id: string,
  active: boolean,
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<CronJobResult>("igloobot.cron.update", { id, isActive: active });
    await loadGlobalProjectCronJobs(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function globalDeleteCronJob(state: IgloobotState, id: string): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<SimpleResult>("igloobot.cron.delete", { id });
    await loadGlobalProjectCronJobs(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function toggleCronJobActive(
  state: IgloobotState,
  id: string,
  active: boolean,
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<CronJobResult>("igloobot.cron.update", { id, isActive: active });
    if (state.igSelectedProjectId) {
      await loadProjectCronJobs(state, state.igSelectedProjectId);
    }
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function deleteCronJobRpc(state: IgloobotState, id: string): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<SimpleResult>("igloobot.cron.delete", { id });
    if (state.igSelectedProjectId) {
      await loadProjectCronJobs(state, state.igSelectedProjectId);
    }
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export function startEditCronJob(state: IgloobotState, job: CronJobEntry): void {
  state.igCronEditingId = job.id;
  state.igCronEditForm = { ...job };
  state.igCronCreateMode = false;
  state.igCronCreateForm = null;
}

export function cancelEditCronJob(state: IgloobotState): void {
  state.igCronEditingId = null;
  state.igCronEditForm = null;
}

export function updateCronEditForm(state: IgloobotState, field: string, value: unknown): void {
  if (!state.igCronEditForm) {
    return;
  }
  (state.igCronEditForm as Record<string, unknown>)[field] = value;
  state.igCronEditForm = { ...state.igCronEditForm };
}

export async function saveEditedCronJob(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igCronEditingId || !state.igCronEditForm) {
    return;
  }
  const form = state.igCronEditForm;
  try {
    const params: Record<string, unknown> = { id: state.igCronEditingId };
    if (form.name !== undefined) {
      params.name = form.name;
    }
    if (form.cronExpression !== undefined) {
      params.cronExpression = form.cronExpression;
    }
    if (form.timezone !== undefined) {
      params.timezone = form.timezone;
    }
    if (form.startAt !== undefined) {
      params.startAt = form.startAt;
    }
    if (form.endAt !== undefined) {
      params.endAt = form.endAt;
    }
    if (form.trigger !== undefined) {
      params.trigger = form.trigger;
    }
    if (form.payload !== undefined) {
      params.payload = form.payload;
    }
    if (form.isActive !== undefined) {
      params.isActive = form.isActive;
    }

    await state.client.request<CronJobResult>("igloobot.cron.update", params);
    state.igCronEditingId = null;
    state.igCronEditForm = null;
    if (state.igSelectedProjectId) {
      await loadProjectCronJobs(state, state.igSelectedProjectId);
    }
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

// --------------- Global Cron CRUD (for the global Cron tab) ---------------

export function globalStartCreateCronJob(state: IgloobotState): void {
  state.igCronCreateMode = true;
  state.igCronCreateForm = {
    projectId: state.igProjects[0]?.id ?? "",
    name: "",
    cronExpression: "",
    timezone: "UTC",
    startAt: null,
    endAt: null,
    trigger: "create_task",
    payload: {},
  };
  state.igCronEditingId = null;
  state.igCronEditForm = null;
}

export function globalCancelCreateCronJob(state: IgloobotState): void {
  state.igCronCreateMode = false;
  state.igCronCreateForm = null;
}

export function globalUpdateCronCreateForm(
  state: IgloobotState,
  field: string,
  value: unknown,
): void {
  if (!state.igCronCreateForm) {
    return;
  }
  (state.igCronCreateForm as Record<string, unknown>)[field] = value;
  state.igCronCreateForm = { ...state.igCronCreateForm };
}

export async function globalSaveNewCronJob(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igCronCreateForm) {
    return;
  }
  const form = state.igCronCreateForm;
  if (!form.name || !form.cronExpression || !form.projectId) {
    state.igProjectsError = "Project, name, and cron expression are required";
    return;
  }
  try {
    await state.client.request<CronJobResult>("igloobot.cron.create", {
      projectId: form.projectId,
      name: form.name,
      cronExpression: form.cronExpression,
      timezone: form.timezone ?? "UTC",
      startAt: form.startAt ?? null,
      endAt: form.endAt ?? null,
      trigger: form.trigger ?? "create_task",
      payload: form.payload ?? {},
    });
    state.igCronCreateMode = false;
    state.igCronCreateForm = null;
    await loadGlobalProjectCronJobs(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export function globalStartEditCronJob(state: IgloobotState, job: CronJobEntry): void {
  state.igCronEditingId = job.id;
  state.igCronEditForm = { ...job };
  state.igCronCreateMode = false;
  state.igCronCreateForm = null;
}

export function globalCancelEditCronJob(state: IgloobotState): void {
  state.igCronEditingId = null;
  state.igCronEditForm = null;
}

export function globalUpdateCronEditForm(
  state: IgloobotState,
  field: string,
  value: unknown,
): void {
  if (!state.igCronEditForm) {
    return;
  }
  (state.igCronEditForm as Record<string, unknown>)[field] = value;
  state.igCronEditForm = { ...state.igCronEditForm };
}

export async function globalSaveEditedCronJob(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igCronEditingId || !state.igCronEditForm) {
    return;
  }
  const form = state.igCronEditForm;
  try {
    const params: Record<string, unknown> = { id: state.igCronEditingId };
    if (form.name !== undefined) {
      params.name = form.name;
    }
    if (form.cronExpression !== undefined) {
      params.cronExpression = form.cronExpression;
    }
    if (form.timezone !== undefined) {
      params.timezone = form.timezone;
    }
    if (form.startAt !== undefined) {
      params.startAt = form.startAt;
    }
    if (form.endAt !== undefined) {
      params.endAt = form.endAt;
    }
    if (form.trigger !== undefined) {
      params.trigger = form.trigger;
    }
    if (form.payload !== undefined) {
      params.payload = form.payload;
    }
    if (form.isActive !== undefined) {
      params.isActive = form.isActive;
    }

    await state.client.request<CronJobResult>("igloobot.cron.update", params);
    state.igCronEditingId = null;
    state.igCronEditForm = null;
    await loadGlobalProjectCronJobs(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

// --------------- Project Session Create ---------------

export function startCreateSession(state: IgloobotState): void {
  state.igSessionCreateMode = true;
}

export function cancelCreateSession(state: IgloobotState): void {
  state.igSessionCreateMode = false;
}

export async function createProjectSession(
  state: IgloobotState,
  type: "chat" | "voice",
  channel: string,
): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) return;
  try {
    await state.client.request<SessionCreateResult>("igloobot.sessions.create", {
      projectId: state.igSelectedProjectId,
      type,
      channel,
    });
    state.igSessionCreateMode = false;
    await loadProjectSessions(state, state.igSelectedProjectId);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

/**
 * Create default sessions for a project via the igloobot.sessions.createDefaults RPC.
 * Called as a fallback when the context picker selects a project with no sessions.
 */
export async function createDefaultProjectSessions(
  state: IgloobotState,
  projectId: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request<{ ok: boolean }>("igloobot.sessions.createDefaults", {
      projectId,
    });
  } catch {
    // Non-critical — createDefaults may not be available
  }
}

// --------------- Knowledge Base Sync ---------------

type KbSyncResult = {
  ok: boolean;
  processed?: number;
  skipped?: number;
  failed?: number;
  totalFiles?: number;
  errors?: string[];
  error?: string;
};

export async function syncKnowledgeBase(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) {
    return;
  }
  state.igKnowledgeBaseSyncing = true;
  state.igKnowledgeBaseSyncResult = null;
  try {
    const res = await state.client.request<KbSyncResult>("igloobot.projects.syncKnowledgeBase", {
      projectId: state.igSelectedProjectId,
    });
    if (res.ok) {
      state.igKnowledgeBaseSyncResult = {
        processed: res.processed ?? 0,
        skipped: res.skipped ?? 0,
        failed: res.failed ?? 0,
        totalFiles: res.totalFiles ?? 0,
        errors: res.errors,
      };
    } else {
      state.igProjectsError = res.error ?? "Knowledge base sync failed";
    }
  } catch (err) {
    state.igProjectsError = String(err);
  } finally {
    state.igKnowledgeBaseSyncing = false;
  }
}

// --------------- Workspace Verification ---------------

type WorkspaceVerifyResult = {
  ok: boolean;
  status?: {
    exists: boolean;
    readable: boolean;
    writable: boolean;
    fileCount?: number;
    error?: string;
  };
  error?: string;
};

export async function verifyWorkspaceAccess(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) {
    return;
  }
  state.igWorkspaceVerifying = true;
  state.igWorkspaceVerifyResult = null;
  try {
    const res = await state.client.request<WorkspaceVerifyResult>(
      "igloobot.projects.verifyWorkspace",
      { projectId: state.igSelectedProjectId },
    );
    if (res.ok && res.status) {
      state.igWorkspaceVerifyResult = res.status;
    } else {
      state.igProjectsError = res.error ?? "Workspace verification failed";
    }
  } catch (err) {
    state.igProjectsError = String(err);
  } finally {
    state.igWorkspaceVerifying = false;
  }
}

// --------------- Multi-Path Folder Management ---------------

/** A folder entry stored in the JSONB arrays. */
export type FolderEntry = { path: string; label?: string };

type FolderTarget = "knowledgeBasePaths" | "workspacePaths";

/** Add a folder entry to the edit form's path array. */
export function addFolderPathToForm(
  state: IgloobotState,
  target: FolderTarget,
  entry: FolderEntry,
): void {
  if (!state.igEditForm) {
    return;
  }
  const form = state.igEditForm as Record<string, unknown>;
  const arr = (form[target] as FolderEntry[] | undefined) ?? [];
  form[target] = [...arr, entry];
  state.igEditForm = { ...state.igEditForm };
}

/** Update a folder entry at a specific index in the edit form. */
export function updateFolderPathInForm(
  state: IgloobotState,
  target: FolderTarget,
  index: number,
  entry: FolderEntry,
): void {
  if (!state.igEditForm) {
    return;
  }
  const form = state.igEditForm as Record<string, unknown>;
  const arr = [...((form[target] as FolderEntry[] | undefined) ?? [])];
  if (index >= 0 && index < arr.length) {
    arr[index] = entry;
  }
  form[target] = arr;
  state.igEditForm = { ...state.igEditForm };
}

/** Remove a folder entry at a specific index in the edit form. */
export function removeFolderPathFromForm(
  state: IgloobotState,
  target: FolderTarget,
  index: number,
): void {
  if (!state.igEditForm) {
    return;
  }
  const form = state.igEditForm as Record<string, unknown>;
  const arr = [...((form[target] as FolderEntry[] | undefined) ?? [])];
  arr.splice(index, 1);
  form[target] = arr;
  state.igEditForm = { ...state.igEditForm };
}

/**
 * Add a folder path directly to a project (for detail tab, outside edit mode).
 * Saves via RPC immediately.
 */
export async function addFolderPathDirect(
  state: IgloobotState,
  target: FolderTarget,
  entry: FolderEntry,
): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) {
    return;
  }
  const project = state.igProjects.find((p) => p.id === state.igSelectedProjectId);
  if (!project) {
    return;
  }
  const existing =
    ((project as Record<string, unknown>)[target] as FolderEntry[] | undefined) ?? [];
  const updated = [...existing, entry];
  try {
    await state.client.request<ProjectResult>("igloobot.projects.update", {
      id: state.igSelectedProjectId,
      [target]: updated,
    });
    await loadProjects(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

/**
 * Remove a folder path directly from a project (for detail tab, outside edit mode).
 * Saves via RPC immediately.
 */
export async function removeFolderPathDirect(
  state: IgloobotState,
  target: FolderTarget,
  index: number,
): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) {
    return;
  }
  const project = state.igProjects.find((p) => p.id === state.igSelectedProjectId);
  if (!project) {
    return;
  }
  const existing = [
    ...(((project as Record<string, unknown>)[target] as FolderEntry[] | undefined) ?? []),
  ];
  existing.splice(index, 1);
  try {
    await state.client.request<ProjectResult>("igloobot.projects.update", {
      id: state.igSelectedProjectId,
      [target]: existing,
    });
    await loadProjects(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

// --------------- Avatar as a Skill Toggle ---------------

export async function toggleAvatarSkill(state: IgloobotState, enabled: boolean): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) return;
  const projectId = state.igSelectedProjectId;
  const existing = state.igProjectSkills.find(
    (s) => s.name === "avatar" || (s.metadata as Record<string, unknown>)?.isAvatar === true,
  );
  try {
    if (enabled && !existing) {
      // Create the avatar skill for this project
      await state.client.request<SkillResult>("igloobot.skills.create", {
        name: "avatar",
        description: "LiveKit + SadTalker avatar video call capability",
        coreInstruction: "This project has avatar video call capability enabled.",
        projectId,
        metadata: { isAvatar: true },
      });
    } else if (enabled && existing && !existing.isActive) {
      // Re-activate existing avatar skill
      await state.client.request<SkillResult>("igloobot.skills.update", {
        id: existing.id,
        isActive: true,
      });
    } else if (!enabled && existing) {
      // Deactivate the avatar skill
      await state.client.request<SkillResult>("igloobot.skills.update", {
        id: existing.id,
        isActive: false,
      });
    }
    await loadProjectSkills(state, projectId);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

// --------------- Project User Add/Remove ---------------

export async function addUserToProjectFromDetail(
  state: IgloobotState,
  userId: string,
  role: "admin" | "project_admin" | "user",
): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) return;
  try {
    await state.client.request<SimpleResult>("igloobot.users.addToProject", {
      userId,
      projectId: state.igSelectedProjectId,
      role,
    });
    await loadProjectUsers(state, state.igSelectedProjectId);
    await loadProjects(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function changeProjectUserRole(
  state: IgloobotState,
  userId: string,
  role: "admin" | "project_admin" | "user",
): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) return;
  try {
    await state.client.request<SimpleResult>("igloobot.users.changeRole", {
      userId,
      projectId: state.igSelectedProjectId,
      role,
    });
    await loadProjectUsers(state, state.igSelectedProjectId);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function removeProjectUser(state: IgloobotState, userId: string): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) return;
  try {
    await state.client.request<SimpleResult>("igloobot.users.removeFromProject", {
      userId,
      projectId: state.igSelectedProjectId,
    });
    await loadProjectUsers(state, state.igSelectedProjectId);
    await loadProjects(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

// --------------- Project Contact CRUD ---------------

export function startCreateContact(state: IgloobotState): void {
  state.igContactCreateMode = true;
  state.igContactEditingId = null;
}

export function cancelContactForm(state: IgloobotState): void {
  state.igContactCreateMode = false;
  state.igContactEditingId = null;
}

export function startEditContact(state: IgloobotState, contactId: string): void {
  state.igContactEditingId = contactId;
  state.igContactCreateMode = false;
}

export async function createProjectContact(
  state: IgloobotState,
  data: { name: string; channel: string; address: string; tags: string[]; notes: string },
): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) return;
  try {
    await state.client.request<ContactResult>("igloobot.contacts.create", {
      projectId: state.igSelectedProjectId,
      ...data,
    });
    state.igContactCreateMode = false;
    await loadProjectContacts(state, state.igSelectedProjectId);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function updateProjectContact(
  state: IgloobotState,
  contactId: string,
  data: {
    name?: string;
    channel?: string;
    address?: string;
    tags?: string[];
    notes?: string | null;
    isActive?: boolean;
  },
): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) return;
  try {
    await state.client.request<ContactResult>("igloobot.contacts.update", {
      id: contactId,
      ...data,
    });
    state.igContactEditingId = null;
    await loadProjectContacts(state, state.igSelectedProjectId);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function deleteProjectContact(state: IgloobotState, contactId: string): Promise<void> {
  if (!state.client || !state.connected || !state.igSelectedProjectId) return;
  try {
    await state.client.request<SimpleResult>("igloobot.contacts.delete", { id: contactId });
    await loadProjectContacts(state, state.igSelectedProjectId);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export function startCreateProject(state: IgloobotState): void {
  state.igSelectedProjectId = null;
  state.igEditMode = true;
  state.igEditForm = {
    name: "",
    description: "",
    systemPrompt: "",
    slug: "",
    agentId: "i-gloobot-helper",
  };
}

export function startEditProject(state: IgloobotState, id: string): void {
  const project = state.igProjects.find((p) => p.id === id);
  if (!project) return;
  state.igEditMode = true;
  state.igEditForm = { ...project };
}

export function cancelEditProject(state: IgloobotState): void {
  state.igEditMode = false;
  state.igEditForm = null;
}

export function updateEditForm(state: IgloobotState, field: string, value: unknown): void {
  if (!state.igEditForm) return;
  (state.igEditForm as Record<string, unknown>)[field] = value;
  state.igEditForm = { ...state.igEditForm };
}

export async function saveProject(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igEditForm) return;
  try {
    const formRec = state.igEditForm as Record<string, unknown>;
    const kbPaths = (formRec.knowledgeBasePaths as FolderEntry[] | undefined) ?? [];
    const wsPaths = (formRec.workspacePaths as FolderEntry[] | undefined) ?? [];
    // Filter out entries with empty paths
    const cleanKb = kbPaths.filter((e) => e.path.trim());
    const cleanWs = wsPaths.filter((e) => e.path.trim());
    if (state.igEditForm.id) {
      await state.client.request<ProjectResult>("igloobot.projects.update", {
        id: state.igEditForm.id,
        name: state.igEditForm.name,
        description: state.igEditForm.description,
        systemPrompt: state.igEditForm.systemPrompt,
        slug: formRec.slug || undefined,
        agentId: typeof formRec.agentId === "string" ? formRec.agentId : undefined,
        knowledgeBasePaths: cleanKb,
        workspacePaths: cleanWs,
      });
    } else {
      await state.client.request<ProjectResult>("igloobot.projects.create", {
        name: state.igEditForm.name,
        description: state.igEditForm.description,
        systemPrompt: state.igEditForm.systemPrompt,
        slug: formRec.slug || undefined,
        agentId: typeof formRec.agentId === "string" ? formRec.agentId : undefined,
        knowledgeBasePaths: cleanKb.length > 0 ? cleanKb : undefined,
        workspacePaths: cleanWs.length > 0 ? cleanWs : undefined,
      });
    }
    state.igEditMode = false;
    state.igEditForm = null;
    await loadProjects(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function deleteProject(state: IgloobotState, id: string): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<SimpleResult>("igloobot.projects.delete", { id });
    if (state.igSelectedProjectId === id) {
      state.igSelectedProjectId = null;
    }
    await loadProjects(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function toggleProjectActive(
  state: IgloobotState,
  id: string,
  active: boolean,
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<ProjectResult>("igloobot.projects.update", { id, isActive: active });
    await loadProjects(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

export async function updateProjectAgent(
  state: IgloobotState,
  projectId: string,
  agentId: string | null,
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<ProjectResult>("igloobot.projects.update", {
      id: projectId,
      agentId: agentId ?? null,
    });
    await loadProjects(state);
  } catch (err) {
    state.igProjectsError = String(err);
  }
}

// --------------- Tasks ---------------

type TasksListResult = { ok: boolean; tasks?: TaskEntry[]; error?: string };
type TaskResult = { ok: boolean; task?: TaskEntry; error?: string };

export async function loadTasks(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) return;
  void loadTemplates(state, "task");
  state.igTasksLoading = true;
  state.igTasksError = null;
  try {
    const params: Record<string, string> = {};
    if (state.igTaskProjectFilter) {
      params.projectId = state.igTaskProjectFilter;
    }
    if (state.igTaskAgentFilter) {
      params.agentId = state.igTaskAgentFilter;
    }
    const res = await state.client.request<TasksListResult>("igloobot.tasks.list", params);
    if (res.ok && res.tasks) {
      state.igTasks = res.tasks;
    } else {
      state.igTasksError = res.error ?? "Failed to load tasks";
    }
  } catch (err) {
    state.igTasksError = friendlyError(err);
  } finally {
    state.igTasksLoading = false;
  }
}

export function startCreateTask(state: IgloobotState): void {
  state.igTaskCreateMode = true;
  state.igSelectedTaskId = null;
  state.igTaskCreateForm = {
    projectId: state.igProjects[0]?.id ?? "",
    title: "",
    type: "custom",
    description: "",
  };
}

export function cancelCreateTask(state: IgloobotState): void {
  state.igTaskCreateMode = false;
  state.igTaskCreateForm = null;
}

export function updateTaskCreateForm(state: IgloobotState, field: string, value: unknown): void {
  if (!state.igTaskCreateForm) return;
  state.igTaskCreateForm[field] = value;
  state.igTaskCreateForm = { ...state.igTaskCreateForm };
}

export async function saveNewTask(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igTaskCreateForm) return;
  const form = state.igTaskCreateForm;
  if (!form.projectId || !form.title) {
    state.igTasksError = "Project and title are required";
    return;
  }
  try {
    await state.client.request<TaskResult>("igloobot.tasks.create", {
      projectId: form.projectId,
      title: form.title,
      type: form.type ?? "custom",
      description: form.description || undefined,
    });
    state.igTaskCreateMode = false;
    state.igTaskCreateForm = null;
    await loadTasks(state);
  } catch (err) {
    state.igTasksError = String(err);
  }
}

export async function cancelTask(state: IgloobotState, id: string): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<TaskResult>("igloobot.tasks.cancel", { id });
    await loadTasks(state);
  } catch (err) {
    state.igTasksError = String(err);
  }
}

export async function retryTask(state: IgloobotState, id: string): Promise<void> {
  if (!state.client || !state.connected) return;
  const original = state.igTasks.find((t) => t.id === id);
  if (!original) return;
  try {
    // Clone the original task with scheduledAt=now so it is immediately dispatched
    await state.client.request<TaskResult>("igloobot.tasks.create", {
      projectId: original.projectId,
      title: original.title,
      type: original.type,
      description: original.description || undefined,
      scheduledAt: new Date().toISOString(),
    });
    await loadTasks(state);
  } catch (err) {
    state.igTasksError = String(err);
  }
}

// --------------- Agent Tasks (Agent panel sub-tab) ---------------

export interface AgentTasksState {
  client: { request<T>(method: string, params: unknown): Promise<T> } | null;
  connected: boolean;
  agentTasksLoading: boolean;
  agentTasksError: string | null;
  agentTasks: TaskEntry[];
}

export async function loadAgentTasks(state: AgentTasksState, agentId: string): Promise<void> {
  if (!state.client || !state.connected) return;
  state.agentTasksLoading = true;
  state.agentTasksError = null;
  try {
    const res = await state.client.request<TasksListResult>("igloobot.tasks.list", { agentId });
    if (res.ok && res.tasks) {
      state.agentTasks = res.tasks;
    } else {
      state.agentTasksError = res.error ?? "Failed to load agent tasks";
    }
  } catch (err) {
    state.agentTasksError = friendlyError(err);
  } finally {
    state.agentTasksLoading = false;
  }
}

// --------------- Users ---------------

type UsersListResult = { ok: boolean; users?: UserEntry[]; error?: string };
type UserDetailResult = {
  ok: boolean;
  user?: UserEntry;
  projects?: UserProjectAssignment[];
  error?: string;
};

export async function loadUsers(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igUsersLoading = true;
  state.igUsersError = null;
  try {
    const res = await state.client.request<UsersListResult>("igloobot.users.list", {});
    if (res.ok && res.users) {
      state.igUsers = res.users;
    } else {
      state.igUsersError = res.error ?? "Failed to load users";
    }
  } catch (err) {
    state.igUsersError = friendlyError(err);
  } finally {
    state.igUsersLoading = false;
  }
}

export async function selectUser(state: IgloobotState, id: string): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igSelectedUserId = id;
  state.igUserEmailCreds = null;
  state.igUserEmailCredsForm = null;
  state.igUserEmailCredsError = null;
  state.igUserEmailCredsTestResult = null;
  try {
    const res = await state.client.request<UserDetailResult>("igloobot.users.get", { id });
    if (res.ok) {
      state.igSelectedUserProjects = res.projects ?? [];
    }
    // Load email credentials for this user
    const credRes = await state.client.request<{
      ok: boolean;
      credentials: import("../views/users-admin.ts").EmailCredEntry | null;
      error?: string;
    }>("igloobot.users.emailCredentials.get", { userId: id });
    if (credRes.ok && credRes.credentials) {
      state.igUserEmailCreds = credRes.credentials;
    }
  } catch (err) {
    state.igUsersError = String(err);
  }
}

async function saveUserEmailCreds(state: IgloobotState, userId: string): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igUserEmailCredsSaving = true;
  state.igUserEmailCredsError = null;
  try {
    const form = state.igUserEmailCredsForm ?? {};
    const existing = state.igUserEmailCreds;
    const res = await state.client.request<{ ok: boolean; error?: string }>(
      "igloobot.users.emailCredentials.set",
      {
        userId,
        type: form.type ?? existing?.type ?? "custom",
        imapHost: form.imapHost ?? existing?.imapHost ?? "",
        imapPort: form.imapPort ?? existing?.imapPort ?? 993,
        imapSecure: form.imapSecure ?? existing?.imapSecure ?? true,
        smtpHost: form.smtpHost ?? existing?.smtpHost ?? "",
        smtpPort: form.smtpPort ?? existing?.smtpPort ?? 587,
        smtpSecure: form.smtpSecure ?? existing?.smtpSecure ?? false,
        username: form.username ?? existing?.username ?? "",
        password: (form as Record<string, unknown>).password ?? undefined,
        fromAddress: form.fromAddress ?? existing?.fromAddress ?? "",
        fromName: form.fromName ?? existing?.fromName ?? "",
        tlsReject: form.tlsReject ?? existing?.tlsReject ?? true,
      },
    );
    if (!res.ok) {
      state.igUserEmailCredsError = res.error ?? "Failed to save email credentials";
    } else {
      state.igUserEmailCredsForm = null;
      // Reload credentials
      const credRes = await state.client.request<{
        ok: boolean;
        credentials: import("../views/users-admin.ts").EmailCredEntry | null;
      }>("igloobot.users.emailCredentials.get", { userId });
      if (credRes.ok && credRes.credentials) {
        state.igUserEmailCreds = credRes.credentials;
      }
    }
  } catch (err) {
    state.igUserEmailCredsError = String(err);
  } finally {
    state.igUserEmailCredsSaving = false;
  }
}

async function deleteUserEmailCreds(state: IgloobotState, userId: string): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request("igloobot.users.emailCredentials.delete", { userId });
    state.igUserEmailCreds = null;
    state.igUserEmailCredsForm = null;
  } catch (err) {
    state.igUserEmailCredsError = String(err);
  }
}

async function testUserEmailCreds(state: IgloobotState, userId: string): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igUserEmailCredsTestResult = null;
  try {
    const res = await state.client.request<{ ok: boolean; message?: string; error?: string }>(
      "igloobot.users.emailCredentials.test",
      { userId },
    );
    if (res.ok) {
      state.igUserEmailCredsTestResult = res.message ?? "IMAP connection successful";
    } else {
      state.igUserEmailCredsError = res.error ?? "IMAP test failed";
    }
  } catch (err) {
    state.igUserEmailCredsError = String(err);
  }
}

export async function changeUserRole(
  state: IgloobotState,
  userId: string,
  projectId: string,
  role: "admin" | "project_admin" | "user",
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<SimpleResult>("igloobot.users.changeRole", {
      userId,
      projectId,
      role,
    });
    // Refresh the user detail to show updated role
    await selectUser(state, userId);
  } catch (err) {
    state.igUsersError = String(err);
  }
}

export async function removeUserFromProject(
  state: IgloobotState,
  userId: string,
  projectId: string,
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<SimpleResult>("igloobot.users.removeFromProject", {
      userId,
      projectId,
    });
    await selectUser(state, userId);
    await loadUsers(state); // Refresh counts
  } catch (err) {
    state.igUsersError = String(err);
  }
}

// --------------- Create User ---------------

export function startCreateUser(state: IgloobotState): void {
  state.igUserCreateMode = true;
  state.igSelectedUserId = null;
  state.igUserCreateForm = {
    firstName: "",
    lastName: "",
    email: "",
    position: "",
    profileDescription: "",
    channels: [],
    preferredChannel: "webchat",
    timezone: "UTC",
  } as Record<string, unknown>;
  (state.igUserCreateForm as Record<string, unknown>).username = "";
  (state.igUserCreateForm as Record<string, unknown>).password = "";
}

export function cancelCreateUser(state: IgloobotState): void {
  state.igUserCreateMode = false;
  state.igUserCreateForm = null;
}

export function updateCreateUserForm(state: IgloobotState, field: string, value: unknown): void {
  if (!state.igUserCreateForm) return;
  (state.igUserCreateForm as Record<string, unknown>)[field] = value;
  state.igUserCreateForm = { ...state.igUserCreateForm };
}

export async function saveNewUser(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igUserCreateForm) return;
  const form = state.igUserCreateForm;
  const formRec = form as Record<string, unknown>;
  if (!formRec.username) {
    state.igUsersError = "Username is required";
    return;
  }
  if (!formRec.password) {
    state.igUsersError = "Password is required";
    return;
  }
  try {
    await state.client.request<SimpleResult>("igloobot.users.create", {
      username: formRec.username || undefined,
      password: formRec.password || undefined,
      firstName: form.firstName || undefined,
      lastName: form.lastName || undefined,
      email: form.email || undefined,
      position: form.position || undefined,
      profileDescription: form.profileDescription || undefined,
      channels: (formRec.channels as unknown[]) ?? [],
      preferredChannel: form.preferredChannel || "webchat",
      timezone: form.timezone || "UTC",
    });
    state.igUserCreateMode = false;
    state.igUserCreateForm = null;
    await loadUsers(state);
  } catch (err) {
    state.igUsersError = String(err);
  }
}

// --------------- Add User to Project ---------------

export async function addUserToProject(
  state: IgloobotState,
  userId: string,
  projectId: string,
  role: "admin" | "project_admin" | "user",
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<SimpleResult>("igloobot.users.addToProject", {
      userId,
      projectId,
      role,
    });
    // Refresh user detail to show new assignment
    await selectUser(state, userId);
    await loadUsers(state);
  } catch (err) {
    state.igUsersError = String(err);
  }
}

// --------------- User Delete ---------------

export async function deleteUser(state: IgloobotState, id: string): Promise<void> {
  if (!state.client || !state.connected) return;
  if (
    !confirm(
      "Delete this user permanently? Project assignments will be removed and other references will be cleared.",
    )
  )
    return;
  try {
    await state.client.request<SimpleResult>("igloobot.users.delete", { id });
    if (state.igSelectedUserId === id) {
      state.igSelectedUserId = null;
      state.igSelectedUserProjects = [];
      state.igUserEditMode = false;
      state.igUserEditForm = null;
    }
    await loadUsers(state);
  } catch (err) {
    state.igUsersError = String(err);
  }
}

// --------------- Reset Password ---------------

export async function resetUserPassword(state: IgloobotState, userId: string): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const newPassword = prompt("Enter new password for this user:");
  if (!newPassword || !newPassword.trim()) {
    return;
  }
  try {
    await state.client.request<SimpleResult>("igloobot.users.resetPassword", {
      id: userId,
      password: newPassword.trim(),
    });
    alert("Password has been reset.");
  } catch (err) {
    state.igUsersError = String(err);
  }
}

// --------------- User Edit ---------------

export function startEditUser(state: IgloobotState): void {
  const user = state.igUsers.find((u) => u.id === state.igSelectedUserId);
  if (!user) return;
  state.igUserEditMode = true;
  state.igUserEditForm = { ...user };
  (state.igUserEditForm as Record<string, unknown>).password = "";
}

export function cancelEditUser(state: IgloobotState): void {
  state.igUserEditMode = false;
  state.igUserEditForm = null;
}

export function updateUserEditForm(state: IgloobotState, field: string, value: unknown): void {
  if (!state.igUserEditForm) return;
  (state.igUserEditForm as Record<string, unknown>)[field] = value;
  state.igUserEditForm = { ...state.igUserEditForm };
}

export async function saveUser(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igUserEditForm) return;
  const form = state.igUserEditForm;
  if (!form.id) return;
  try {
    const editRec = form as Record<string, unknown>;
    const updatePayload: Record<string, unknown> = {
      id: form.id,
      firstName: form.firstName ?? "",
      lastName: form.lastName ?? "",
      email: form.email ?? "",
      position: form.position ?? "",
      profileDescription: form.profileDescription ?? "",
      channels: (editRec.channels as unknown[]) ?? [],
      username: editRec.username ?? "",
      preferredChannel: form.preferredChannel ?? "",
      timezone: form.timezone ?? "UTC",
    };
    // Only send password if user entered a new one
    if (editRec.password && String(editRec.password).trim()) {
      updatePayload.password = String(editRec.password).trim();
    }
    // Include metadata (desktop URLs, etc.) if present
    if (editRec.metadata && typeof editRec.metadata === "object") {
      updatePayload.metadata = editRec.metadata;
    }
    await state.client.request<SimpleResult>("igloobot.users.update", updatePayload);
    state.igUserEditMode = false;
    state.igUserEditForm = null;
    await loadUsers(state);
    // Re-select user to refresh detail
    if (state.igSelectedUserId) {
      await selectUser(state, state.igSelectedUserId);
    }
  } catch (err) {
    state.igUsersError = String(err);
  }
}

// --------------- MCP Skills ---------------

type SkillsListResult = { ok: boolean; skills?: SkillEntry[]; error?: string };
type SkillResult = { ok: boolean; skill?: SkillEntry; error?: string };

export async function loadMcpSkills(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igMcpSkillsLoading = true;
  state.igMcpSkillsError = null;
  try {
    const res = await state.client.request<SkillsListResult>("igloobot.skills.list", {
      activeOnly: false,
    });
    if (res.ok && res.skills) {
      // Fetch skills.status to get the /command config per filesystem skill key
      let cmdByKey = new Map<string, boolean | undefined>();
      try {
        const statusRes = await state.client.request<{ skills: Array<{ skillKey: string; command?: boolean }> }>(
          "skills.status",
          {},
        );
        if (statusRes?.skills) {
          cmdByKey = new Map(statusRes.skills.map((s) => [s.skillKey, s.command]));
        }
      } catch {
        // skills.status failure is non-fatal; command stays undefined
      }
      state.igMcpSkills = res.skills.map((skill) => ({
        ...skill,
        command: cmdByKey.get(skill.name),
      }));
    } else {
      state.igMcpSkillsError = res.error ?? "Failed to load skills";
    }
  } catch (err) {
    state.igMcpSkillsError = friendlyError(err);
  } finally {
    state.igMcpSkillsLoading = false;
  }
}

export function selectMcpSkill(state: IgloobotState, id: string): void {
  state.igMcpSelectedSkillId = id;
  state.igMcpSkillEditMode = false;
  state.igMcpSkillCreateMode = false;
  state.igMcpSkillEditForm = null;
}

export function startCreateMcpSkill(state: IgloobotState): void {
  state.igMcpSelectedSkillId = null;
  state.igMcpSkillEditMode = false;
  state.igMcpSkillCreateMode = true;
  state.igMcpSkillEditForm = {
    name: "",
    description: "",
    coreInstruction: "",
    adminInstruction: "",
    botInstruction: "",
    templateType: "skill",
    projectId: null,
    metadata: {},
  };
}

export function startEditMcpSkill(state: IgloobotState, id: string): void {
  const skill = state.igMcpSkills.find((s) => s.id === id);
  if (!skill) return;
  state.igMcpSkillEditMode = true;
  state.igMcpSkillCreateMode = false;
  state.igMcpSkillEditForm = { ...skill };
}

export function cancelEditMcpSkill(state: IgloobotState): void {
  state.igMcpSkillEditMode = false;
  state.igMcpSkillCreateMode = false;
  state.igMcpSkillEditForm = null;
}

export function updateMcpSkillForm(state: IgloobotState, field: string, value: unknown): void {
  if (!state.igMcpSkillEditForm) return;
  (state.igMcpSkillEditForm as Record<string, unknown>)[field] = value;
  state.igMcpSkillEditForm = { ...state.igMcpSkillEditForm };
}

export async function saveMcpSkill(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igMcpSkillEditForm) return;
  const form = state.igMcpSkillEditForm;
  try {
    if (state.igMcpSkillCreateMode) {
      await state.client.request<SkillResult>("igloobot.skills.create", {
        name: form.name,
        description: form.description,
        coreInstruction: form.coreInstruction,
        adminInstruction: form.adminInstruction,
        botInstruction: form.botInstruction,
        executionType: form.executionType,
        templateType: form.templateType ?? "skill",
        projectId: form.projectId || undefined,
        metadata: form.metadata,
      });
    } else if (form.id) {
      await state.client.request<SkillResult>("igloobot.skills.update", {
        id: form.id,
        name: form.name,
        description: form.description,
        coreInstruction: form.coreInstruction,
        adminInstruction: form.adminInstruction,
        botInstruction: form.botInstruction,
        executionType: form.executionType,
        templateType: form.templateType,
        metadata: form.metadata,
      });
    }
    state.igMcpSkillEditMode = false;
    state.igMcpSkillCreateMode = false;
    state.igMcpSkillEditForm = null;
    await loadMcpSkills(state);
  } catch (err) {
    state.igMcpSkillsError = String(err);
  }
}

export async function deleteMcpSkill(state: IgloobotState, id: string): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<SimpleResult>("igloobot.skills.delete", { id });
    if (state.igMcpSelectedSkillId === id) {
      state.igMcpSelectedSkillId = null;
    }
    await loadMcpSkills(state);
  } catch (err) {
    state.igMcpSkillsError = String(err);
  }
}

export async function toggleMcpSkillActive(
  state: IgloobotState,
  id: string,
  active: boolean,
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<SkillResult>("igloobot.skills.update", { id, isActive: active });
    await loadMcpSkills(state);
  } catch (err) {
    state.igMcpSkillsError = String(err);
  }
}

export async function toggleMcpSkillCommand(
  state: IgloobotState,
  skillKey: string,
  command: boolean,
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request("skills.update", { skillKey, command });
    await loadMcpSkills(state);
  } catch (err) {
    state.igMcpSkillsError = String(err);
  }
}

export async function syncMcpSkillsFromFilesystem(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.igMcpSkillsSyncLoading = true;
  state.igMcpSkillsSyncResult = null;
  try {
    const res = await state.client.request<{
      ok: boolean;
      imported?: number;
      skipped?: number;
      error?: string;
    }>("igloobot.skills.syncFromFilesystem", {});
    if (res.ok) {
      state.igMcpSkillsSyncResult = {
        imported: res.imported ?? 0,
        skipped: res.skipped ?? 0,
      };
      // Refresh the skills list after sync
      await loadMcpSkills(state);
    } else {
      state.igMcpSkillsError = res.error ?? "Sync failed";
    }
  } catch (err) {
    state.igMcpSkillsError = friendlyError(err);
  } finally {
    state.igMcpSkillsSyncLoading = false;
  }
}

// --------------- Avatar ---------------

type AvatarStatusResult = { ok: boolean; status?: AvatarServiceStatus; error?: string };
type AvatarConfigResult = { ok: boolean; config?: AvatarConfig; error?: string };
type AvatarSessionsResult = { ok: boolean; sessions?: AvatarSessionEntry[]; error?: string };

export async function loadAvatarStatus(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igAvatarLoading = true;
  state.igAvatarError = null;
  try {
    const res = await state.client.request<AvatarStatusResult>("igloobot.avatar.status", {});
    if (res.ok && res.status) {
      state.igAvatarStatus = res.status;
    }
  } catch (err) {
    state.igAvatarError = friendlyError(err);
  } finally {
    state.igAvatarLoading = false;
  }
}

export async function loadAvatarConfig(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    const res = await state.client.request<AvatarConfigResult>("igloobot.avatar.config.get", {});
    if (res.ok && res.config) {
      state.igAvatarConfig = res.config;
    }
  } catch (err) {
    state.igAvatarError = String(err);
  }
}

export async function loadAvatarSessions(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igAvatarLoading = true;
  try {
    const res = await state.client.request<AvatarSessionsResult>(
      "igloobot.avatar.sessions.list",
      {},
    );
    if (res.ok && res.sessions) {
      state.igAvatarSessions = res.sessions;
    }
  } catch (err) {
    state.igAvatarError = String(err);
  } finally {
    state.igAvatarLoading = false;
  }
}

export async function endAvatarSessionRpc(state: IgloobotState, id: string): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<SimpleResult>("igloobot.avatar.sessions.end", { id });
    await loadAvatarSessions(state);
    await loadAvatarStatus(state);
  } catch (err) {
    state.igAvatarError = String(err);
  }
}

export function startEditAvatarConfig(state: IgloobotState): void {
  if (!state.igAvatarConfig) return;
  state.igAvatarConfigEditMode = true;
  state.igAvatarConfigForm = { ...state.igAvatarConfig };
}

export function cancelEditAvatarConfig(state: IgloobotState): void {
  state.igAvatarConfigEditMode = false;
  state.igAvatarConfigForm = null;
}

export function updateAvatarConfigForm(state: IgloobotState, field: string, value: string): void {
  if (!state.igAvatarConfigForm) return;
  (state.igAvatarConfigForm as Record<string, unknown>)[field] = value;
  state.igAvatarConfigForm = { ...state.igAvatarConfigForm };
}

export async function saveAvatarConfig(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igAvatarConfigForm) return;
  try {
    const payload: Record<string, string | undefined> = {
      livekitUrl: state.igAvatarConfigForm.livekitUrl,
      sadtalkerUrl: state.igAvatarConfigForm.sadtalkerUrl,
      avatarImagePath: state.igAvatarConfigForm.avatarImagePath,
      defaultTtsVoice: state.igAvatarConfigForm.defaultTtsVoice,
      llmModel: state.igAvatarConfigForm.llmModel,
      workspaceDir: state.igAvatarConfigForm.workspaceDir,
    };
    // Only send API key/secret if the user actually typed something
    // (blank means "keep existing value")
    if (state.igAvatarConfigForm.livekitApiKey) {
      payload.livekitApiKey = state.igAvatarConfigForm.livekitApiKey;
    }
    if (state.igAvatarConfigForm.livekitApiSecret) {
      payload.livekitApiSecret = state.igAvatarConfigForm.livekitApiSecret;
    }
    const res = await state.client.request<AvatarConfigResult>(
      "igloobot.avatar.config.update",
      payload,
    );
    if (res.ok && res.config) {
      state.igAvatarConfig = res.config;
    }
    state.igAvatarConfigEditMode = false;
    state.igAvatarConfigForm = null;
  } catch (err) {
    state.igAvatarError = String(err);
  }
}

export function selectAvatarSession(state: IgloobotState, id: string): void {
  state.igAvatarSelectedSessionId = id;
}

// --------------- Project Sessions ---------------

type SessionsListResult = { ok: boolean; sessions?: ProjectSessionEntry[]; error?: string };
type SessionResult = { ok: boolean; session?: ProjectSessionEntry; error?: string };

export async function loadProjectSessions(state: IgloobotState, projectId: string): Promise<void> {
  if (!state.client || !state.connected) return;
  state.igProjectSessionsLoading = true;
  try {
    const res = await state.client.request<SessionsListResult>("igloobot.sessions.list", {
      projectId,
      limit: 50,
    });
    if (res.ok && res.sessions) {
      state.igProjectSessions = res.sessions;
    }
  } catch {
    // Non-critical — sessions may not be populated yet
    state.igProjectSessions = [];
  } finally {
    state.igProjectSessionsLoading = false;
  }
}

export async function completeSessionRpc(
  state: IgloobotState,
  sessionId: string,
  status: "completed" | "failed",
): Promise<void> {
  if (!state.client || !state.connected) return;
  try {
    await state.client.request<SessionResult>("igloobot.sessions.complete", {
      id: sessionId,
      status,
    });
    // Reload sessions for the currently selected project
    if (state.igSelectedProjectId) {
      await loadProjectSessions(state, state.igSelectedProjectId);
    }
  } catch {
    // Non-critical
  }
}

// --------------- Props Builders ---------------

export function buildProjectsProps(state: IgloobotState): ProjectsProps {
  const selectedProject = state.igSelectedProjectId
    ? (state.igProjects.find((p) => p.id === state.igSelectedProjectId) ?? null)
    : null;

  return {
    loading: state.igProjectsLoading,
    error: state.igProjectsError,
    projects: state.igProjects,
    selectedProjectId: state.igSelectedProjectId,
    selectedProject,
    editMode: state.igEditMode,
    editForm: state.igEditForm,
    sessions: state.igProjectSessions,
    sessionsLoading: state.igProjectSessionsLoading,
    // Detail panel sub-tabs
    detailPanel: state.igProjectDetailPanel,
    projectUsers: state.igProjectUsers,
    projectUsersLoading: state.igProjectUsersLoading,
    projectSkills: state.igProjectSkills,
    projectSkillsLoading: state.igProjectSkillsLoading,
    projectCronJobs: state.igProjectCronJobs,
    projectCronJobsLoading: state.igProjectCronJobsLoading,
    projectTasks: state.igProjectTasks,
    projectTasksLoading: state.igProjectTasksLoading,
    projectContacts: state.igProjectContacts,
    projectContactsLoading: state.igProjectContactsLoading,
    contactCreateMode: state.igContactCreateMode,
    contactEditingId: state.igContactEditingId,
    allUsers: state.igUsers,
    sessionCreateMode: state.igSessionCreateMode,
    agents: state.agentsList?.agents ?? [],
    // Callbacks
    onRefresh: () => void loadProjects(state),
    onSelectProject: (id) => {
      selectProject(state, id);
      void loadProjectSessions(state, id);
    },
    onCreateProject: () => startCreateProject(state),
    onEditProject: (id) => startEditProject(state, id),
    onSaveProject: () => void saveProject(state),
    onCancelEdit: () => cancelEditProject(state),
    onToggleActive: (id, active) => void toggleProjectActive(state, id, active),
    onDeleteProject: (id) => void deleteProject(state, id),
    onFormChange: (field, value) => updateEditForm(state, field, value),
    onCompleteSession: (sessionId, status) => void completeSessionRpc(state, sessionId, status),
    onSetDetailPanel: (panel) => setProjectDetailPanel(state, panel),
    // User management
    onAddUser: (userId, role) => void addUserToProjectFromDetail(state, userId, role),
    onChangeUserRole: (userId, role) => void changeProjectUserRole(state, userId, role),
    onRemoveUser: (userId) => void removeProjectUser(state, userId),
    // Contact management
    onCreateContact: (data) => void createProjectContact(state, data),
    onUpdateContact: (id, data) => void updateProjectContact(state, id, data),
    onDeleteContact: (id) => void deleteProjectContact(state, id),
    onStartCreateContact: () => startCreateContact(state),
    onCancelContactForm: () => cancelContactForm(state),
    onStartEditContact: (id) => startEditContact(state, id),
    // Session management
    onCreateSession: () => startCreateSession(state),
    onCancelCreateSession: () => cancelCreateSession(state),
    onSaveNewSession: (type, channel) => void createProjectSession(state, type, channel),
    // Avatar as a skill
    onToggleAvatar: (enabled) => void toggleAvatarSkill(state, enabled),
    // Agent assignment
    onUpdateProjectAgent: (projectId, agentId) =>
      void updateProjectAgent(state, projectId, agentId),
    // Knowledge Base
    knowledgeBaseSyncing: state.igKnowledgeBaseSyncing,
    knowledgeBaseSyncResult: state.igKnowledgeBaseSyncResult,
    onSyncKnowledgeBase: () => void syncKnowledgeBase(state),
    // Workspace
    workspaceVerifying: state.igWorkspaceVerifying,
    workspaceVerifyResult: state.igWorkspaceVerifyResult,
    onVerifyWorkspace: () => void verifyWorkspaceAccess(state),
    // Multi-path folder management
    onAddFolderPath: (target, entry) => addFolderPathToForm(state, target, entry),
    onUpdateFolderPath: (target, index, entry) =>
      updateFolderPathInForm(state, target, index, entry),
    onRemoveFolderPath: (target, index) => removeFolderPathFromForm(state, target, index),
    onAddFolderPathDirect: (target, entry) => void addFolderPathDirect(state, target, entry),
    onRemoveFolderPathDirect: (target, index) => void removeFolderPathDirect(state, target, index),
  };
}

export function buildTasksProps(state: IgloobotState): TasksAdminProps {
  return {
    loading: state.igTasksLoading,
    error: state.igTasksError,
    tasks: state.igTasks,
    statusFilter: state.igTaskStatusFilter,
    typeFilter: state.igTaskTypeFilter,
    projectFilter: state.igTaskProjectFilter,
    agentFilter: state.igTaskAgentFilter,
    selectedTask: state.igSelectedTaskId
      ? (state.igTasks.find((t) => t.id === state.igSelectedTaskId) ?? null)
      : null,
    createMode: state.igTaskCreateMode,
    createForm: state.igTaskCreateForm,
    taskTemplates: state.igTaskTemplates ?? [],
    projects: state.igProjects,
    agents: (state.agentsList?.agents ?? []).map((a) => ({
      id: a.id,
      name: a.name ?? a.identity?.name ?? a.id,
    })),
    onRefresh: () => void loadTasks(state),
    onFilterStatus: (s) => {
      state.igTaskStatusFilter = s;
    },
    onFilterType: (t) => {
      state.igTaskTypeFilter = t;
    },
    onFilterProject: (projectId) => {
      state.igTaskProjectFilter = projectId;
      void loadTasks(state);
    },
    onFilterAgent: (agentId) => {
      state.igTaskAgentFilter = agentId;
      void loadTasks(state);
    },
    onSelectTask: (id) => {
      state.igSelectedTaskId = state.igSelectedTaskId === id ? null : id;
    },
    onCreateTask: () => startCreateTask(state),
    onCancelCreate: () => cancelCreateTask(state),
    onCreateFormChange: (field, value) => updateTaskCreateForm(state, field, value),
    onSaveNewTask: () => void saveNewTask(state),
    onCancelTask: (id) => void cancelTask(state, id),
    onRetryTask: (id) => void retryTask(state, id),
  };
}

export function buildUsersProps(state: IgloobotState): UsersAdminProps {
  return {
    loading: state.igUsersLoading,
    error: state.igUsersError,
    users: state.igUsers,
    searchQuery: state.igUserSearchQuery,
    selectedUser: state.igSelectedUserId
      ? (state.igUsers.find((u) => u.id === state.igSelectedUserId) ?? null)
      : null,
    selectedUserProjects: state.igSelectedUserProjects,
    editMode: state.igUserEditMode,
    editForm: state.igUserEditForm,
    createMode: state.igUserCreateMode,
    createForm: state.igUserCreateForm,
    projects: state.igProjects.map((p) => ({ id: p.id, name: p.name })),
    onRefresh: () => void loadUsers(state),
    onSearchChange: (q) => {
      state.igUserSearchQuery = q;
    },
    onSelectUser: (id) => void selectUser(state, id),
    onCreateUser: () => startCreateUser(state),
    onSaveNewUser: () => void saveNewUser(state),
    onCancelCreate: () => cancelCreateUser(state),
    onCreateFormChange: (field, value) => updateCreateUserForm(state, field, value),
    onAddToProject: (userId, projectId, role) =>
      void addUserToProject(state, userId, projectId, role),
    onChangeRole: (userId, projectId, role) => void changeUserRole(state, userId, projectId, role),
    onRemoveFromProject: (userId, projectId) =>
      void removeUserFromProject(state, userId, projectId),
    onDeleteUser: (id) => void deleteUser(state, id),
    onEditUser: () => startEditUser(state),
    onCancelEdit: () => cancelEditUser(state),
    onSaveUser: () => void saveUser(state),
    onEditFormChange: (field, value) => updateUserEditForm(state, field, value),
    onResetPassword: (id) => void resetUserPassword(state, id),
    emailCreds: state.igUserEmailCreds,
    emailCredsLoading: state.igUserEmailCredsLoading,
    emailCredsSaving: state.igUserEmailCredsSaving,
    emailCredsError: state.igUserEmailCredsError,
    emailCredsTestResult: state.igUserEmailCredsTestResult,
    emailCredsForm: state.igUserEmailCredsForm,
    onEmailCredsFormChange: (field, value) => {
      if (!state.igUserEmailCredsForm) {
        state.igUserEmailCredsForm = {};
      }
      (state.igUserEmailCredsForm as Record<string, unknown>)[field] = value;
      state.igUserEmailCredsForm = { ...state.igUserEmailCredsForm };
    },
    onEmailCredsSave: (userId) => void saveUserEmailCreds(state, userId),
    onEmailCredsDelete: (userId) => void deleteUserEmailCreds(state, userId),
    onEmailCredsTest: (userId) => void testUserEmailCreds(state, userId),
    onEmailCredsGmailPreset: () => {
      state.igUserEmailCredsForm = {
        ...state.igUserEmailCredsForm,
        type: "gmail",
        imapHost: "imap.gmail.com",
        imapPort: 993,
        imapSecure: true,
        smtpHost: "smtp.gmail.com",
        smtpPort: 587,
        smtpSecure: false,
      };
    },
  };
}

export function buildMcpSkillsProps(state: IgloobotState): SkillsAdminProps {
  const selectedSkill = state.igMcpSelectedSkillId
    ? (state.igMcpSkills.find((s) => s.id === state.igMcpSelectedSkillId) ?? null)
    : null;

  return {
    loading: state.igMcpSkillsLoading,
    error: state.igMcpSkillsError,
    skills: state.igMcpSkills,
    searchQuery: state.igMcpSkillsSearchQuery,
    statusFilter: state.igMcpSkillsStatusFilter,
    categoryFilter: state.igMcpSkillsCategoryFilter,
    selectedSkill,
    editMode: state.igMcpSkillEditMode,
    editForm: state.igMcpSkillEditForm,
    createMode: state.igMcpSkillCreateMode,
    projects: state.igProjects.map((p) => ({ id: p.id, name: p.name })),
    onRefresh: () => void loadMcpSkills(state),
    onSearchChange: (q) => {
      state.igMcpSkillsSearchQuery = q;
    },
    onStatusFilterChange: (f) => {
      state.igMcpSkillsStatusFilter = f;
    },
    onCategoryFilterChange: (f) => {
      state.igMcpSkillsCategoryFilter = f;
    },
    onSelectSkill: (id) => selectMcpSkill(state, id),
    onCreateSkill: () => startCreateMcpSkill(state),
    onEditSkill: (id) => startEditMcpSkill(state, id),
    onSaveSkill: () => void saveMcpSkill(state),
    onCancelEdit: () => cancelEditMcpSkill(state),
    onToggleActive: (id, active) => void toggleMcpSkillActive(state, id, active),
    onToggleCommand: (skillKey, command) => void toggleMcpSkillCommand(state, skillKey, command),
    onDeleteSkill: (id) => void deleteMcpSkill(state, id),
    onFormChange: (field, value) => updateMcpSkillForm(state, field, value),
    onSyncFromFilesystem: () => void syncMcpSkillsFromFilesystem(state),
    syncLoading: state.igMcpSkillsSyncLoading,
    syncResult: state.igMcpSkillsSyncResult,
  };
}

export function buildAvatarProps(
  state: IgloobotState,
  modelCatalog?: Array<{ id: string; name: string; provider: string }>,
): AvatarAdminProps {
  const selectedSession = state.igAvatarSelectedSessionId
    ? (state.igAvatarSessions.find((s) => s.id === state.igAvatarSelectedSessionId) ?? null)
    : null;

  return {
    loading: state.igAvatarLoading,
    error: state.igAvatarError,
    status: state.igAvatarStatus,
    config: state.igAvatarConfig,
    sessions: state.igAvatarSessions,
    configEditMode: state.igAvatarConfigEditMode,
    configForm: state.igAvatarConfigForm,
    selectedSession,
    statusFilter: state.igAvatarStatusFilter,
    modelCatalog: modelCatalog ?? [],
    onRefresh: () => void loadAvatarSessions(state),
    onRefreshStatus: () => void loadAvatarStatus(state),
    onSelectSession: (id) => selectAvatarSession(state, id),
    onEndSession: (id) => void endAvatarSessionRpc(state, id),
    onEditConfig: () => startEditAvatarConfig(state),
    onSaveConfig: () => void saveAvatarConfig(state),
    onCancelEdit: () => cancelEditAvatarConfig(state),
    onConfigFormChange: (field, value) => updateAvatarConfigForm(state, field, value),
    onStatusFilterChange: (status) => {
      state.igAvatarStatusFilter = status;
    },
  };
}

// --------------- Stats (Overview) ---------------

type StatsResult = {
  ok: boolean;
  stats?: {
    projects: number;
    users: number;
    tasks: number;
    messages: number;
    sessions: number;
    avatarSessions: number;
    skills: number;
    n8nWorkflows: number;
    cronJobs: number;
    documents: number;
    emailConfigured: boolean;
  };
  error?: string;
};

/**
 * Fetch i-GlooBot platform stats for the Overview page.
 * Uses a host object that has igStats + igStatsAvailable properties.
 */
export async function loadIgloobotStats(host: {
  client: { request<T>(method: string, params: unknown): Promise<T> } | null;
  connected: boolean;
  igStats: StatsResult["stats"] | null;
  igStatsAvailable: boolean;
}): Promise<void> {
  if (!host.client || !host.connected) return;
  try {
    const res = await host.client.request<StatsResult>("igloobot.stats", {});
    if (res.ok && res.stats) {
      host.igStats = res.stats;
      host.igStatsAvailable = true;
    } else {
      host.igStatsAvailable = false;
    }
  } catch {
    // Plugin not loaded or DB not configured — silently degrade
    host.igStatsAvailable = false;
    host.igStats = null;
  }
}

// --------------- Company (Multi-Company) ---------------

type CompanyResult = { ok: boolean; company?: CompanyEntry; error?: string };
type CompaniesResult = { ok: boolean; companies?: CompanyEntry[]; error?: string };

/** Load the list of all companies. */
export async function loadCompanies(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.igCompaniesLoading = true;
  try {
    const res = await state.client.request<CompaniesResult>("igloobot.company.list", {});
    if (res.ok && res.companies) {
      state.igCompanies = res.companies;
      // Sync the active company ID from the list
      const active = res.companies.find((c) => c.isActive);
      if (active) {
        state.igActiveCompanyId = active.id;
      }
    }
  } catch {
    // Non-critical — the active company load will still work
  } finally {
    state.igCompaniesLoading = false;
  }
}

/** Load the active company details into the form. */
export async function loadCompany(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.igCompanyLoading = true;
  state.igCompanyError = null;
  try {
    const res = await state.client.request<CompanyResult>("igloobot.company.get", {});
    if (res.ok && res.company) {
      state.igCompany = res.company;
      state.igCompanyForm = { ...res.company };
      state.igActiveCompanyId = res.company.id;
      // Load VAT numbers for this company
      try {
        const vatRes = await state.client.request<{ ok: boolean; vatNumbers: typeof state.igCompanyVatNumbers }>(
          "igloobot.company.vatNumbers.list",
          { companyId: res.company.id },
        );
        if (vatRes.ok && vatRes.vatNumbers) {
          state.igCompanyVatNumbers = vatRes.vatNumbers;
        }
      } catch {
        // Non-critical
      }
    } else {
      state.igCompanyError = res.error ?? "Failed to load company details";
    }
  } catch (err) {
    state.igCompanyError = friendlyError(err);
  } finally {
    state.igCompanyLoading = false;
  }
}

/** Save the currently edited company form. */
export async function saveCompany(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igCompanyForm) {
    return;
  }
  state.igCompanySaving = true;
  state.igCompanyError = null;
  try {
    const form = state.igCompanyForm;
    const res = await state.client.request<CompanyResult>("igloobot.company.update", {
      id: form.id ?? state.igActiveCompanyId ?? undefined,
      name: form.name ?? "",
      description: form.description ?? "",
      address: form.address ?? "",
      city: form.city ?? "",
      state: form.state ?? "",
      zipCode: form.zipCode ?? "",
      country: form.country ?? "",
      vatNumber: form.vatNumber ?? "",
      website: form.website ?? "",
      email: form.email ?? "",
      phone: form.phone ?? "",
    });
    if (res.ok && res.company) {
      state.igCompany = res.company;
      state.igCompanyForm = { ...res.company };
      // Also update the company in the list
      state.igCompanies = state.igCompanies.map((c) =>
        c.id === res.company!.id ? res.company! : c,
      );
    } else {
      state.igCompanyError = res.error ?? "Failed to save company details";
    }
  } catch (err) {
    state.igCompanyError = friendlyError(err);
  } finally {
    state.igCompanySaving = false;
  }
}

/** Create a new blank company. */
export async function createNewCompany(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.igCompanyError = null;
  try {
    const res = await state.client.request<CompanyResult>("igloobot.company.create", {});
    if (res.ok && res.company) {
      // Switch to the newly created company
      await switchActiveCompany(state, res.company.id);
    } else {
      state.igCompanyError = res.error ?? "Failed to create company";
    }
  } catch (err) {
    state.igCompanyError = friendlyError(err);
  }
}

/** Switch the active company and reload the form. */
export async function switchActiveCompany(state: IgloobotState, id: string): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.igCompanyLoading = true;
  state.igCompanyError = null;
  try {
    const res = await state.client.request<CompanyResult>("igloobot.company.setActive", { id });
    if (res.ok && res.company) {
      state.igCompany = res.company;
      state.igCompanyForm = { ...res.company };
      state.igActiveCompanyId = res.company.id;

      // ---- Clear ALL company-scoped state ----
      const s = state as unknown as Record<string, unknown>;

      // Chat state
      s.chatProjectId = null;
      s.chatProjectSessions = [];

      // Project-scoped arrays
      state.igProjectSessions = [];
      state.igProjectUsers = [];
      state.igProjectSkills = [];
      state.igProjectContacts = [];
      state.igProjectTasks = [];
      state.igProjectCronJobs = [];

      // Agent definitions
      s.agentDefs = [];

      // Channel configs
      s.ccAllConfigs = [];

      // ---- Reload all data for the new company ----
      void loadCompanies(state);
      void loadProjects(state);
      void loadTasks(state);
      void loadUsers(state);
      void loadIgloobotStats(state as unknown as Parameters<typeof loadIgloobotStats>[0]);

      // Reload agent definitions and channel configs
      void loadAllChannelConfigs(state as unknown as ChannelConfigsState);
      void loadAgentDefinitions(state as unknown as AgentDefinitionsState);
    } else {
      state.igCompanyError = res.error ?? "Failed to switch company";
    }
  } catch (err) {
    state.igCompanyError = friendlyError(err);
  } finally {
    state.igCompanyLoading = false;
  }
}

export function updateCompanyForm(state: IgloobotState, field: string, value: string): void {
  if (!state.igCompanyForm) {
    return;
  }
  (state.igCompanyForm as Record<string, unknown>)[field] = value;
  state.igCompanyForm = { ...state.igCompanyForm };
}

export function buildCompanyProps(state: IgloobotState): CompanyProps {
  return {
    loading: state.igCompanyLoading,
    error: state.igCompanyError,
    saving: state.igCompanySaving,
    company: state.igCompany,
    form: state.igCompanyForm,
    companies: state.igCompanies,
    activeCompanyId: state.igActiveCompanyId,
    onRefresh: () => {
      void loadCompanies(state);
      void loadCompany(state);
    },
    onSave: () => void saveCompany(state),
    onFormChange: (field, value) => updateCompanyForm(state, field, value),
    onSelectCompany: (id) => void switchActiveCompany(state, id),
    onCreateCompany: () => void createNewCompany(state),
    vatNumbers: state.igCompanyVatNumbers ?? [],
    onAddVatNumber: (country, vatNumber, vatRate) => {
      if (!state.igActiveCompanyId) return;
      void (async () => {
        try {
          await state.client?.request("igloobot.company.vatNumbers.set", {
            companyId: state.igActiveCompanyId,
            country,
            vatNumber,
            vatRate,
          });
          const res = await state.client?.request<{ ok: boolean; vatNumbers: typeof state.igCompanyVatNumbers }>(
            "igloobot.company.vatNumbers.list",
            { companyId: state.igActiveCompanyId },
          );
          if (res?.ok && res.vatNumbers) {
            state.igCompanyVatNumbers = res.vatNumbers;
          }
        } catch (err) {
          state.igCompanyError = String(err);
        }
      })();
    },
    onDeleteVatNumber: (id) => {
      void (async () => {
        try {
          await state.client?.request("igloobot.company.vatNumbers.delete", { id });
          if (state.igActiveCompanyId) {
            const res = await state.client?.request<{ ok: boolean; vatNumbers: typeof state.igCompanyVatNumbers }>(
              "igloobot.company.vatNumbers.list",
              { companyId: state.igActiveCompanyId },
            );
            if (res?.ok && res.vatNumbers) {
              state.igCompanyVatNumbers = res.vatNumbers;
            }
          }
        } catch (err) {
          state.igCompanyError = String(err);
        }
      })();
    },
  };
}

// --------------- Email Config ---------------

type EmailConfigResult = { ok: boolean; config: EmailConfigEntry | null; error?: string };
type EmailTestRpcResult = { ok: boolean; results: EmailTestResult; error?: string };

type EmailStatusResult = { ok: boolean; status?: EmailServiceStatus; error?: string };

export async function loadEmailStatus(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const agentId = state.igEmailSelectedAgentId;
  if (!agentId) {
    return;
  }
  try {
    const res = await state.client.request<EmailStatusResult>("igloobot.email.status", { agentId });
    if (res.ok && res.status) {
      state.igEmailStatus = res.status;
    }
  } catch {
    // Non-critical — status endpoint may not be available yet
    state.igEmailStatus = null;
  }
}

export async function loadEmailConfig(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const agentId = state.igEmailSelectedAgentId;
  if (!agentId) {
    return;
  }
  state.igEmailLoading = true;
  state.igEmailError = null;
  try {
    const res = await state.client.request<EmailConfigResult>("igloobot.email.getConfig", {
      agentId,
    });
    if (res.ok) {
      // config can be null when agent has no email configured yet
      state.igEmailConfig = res.config ?? null;
      state.igEmailForm = res.config ? { ...res.config } : { agentId };
    } else {
      state.igEmailError = res.error ?? "Failed to load email config";
    }
  } catch (err) {
    state.igEmailError = friendlyError(err);
  } finally {
    state.igEmailLoading = false;
  }
}

export async function saveEmailConfig(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected || !state.igEmailForm) {
    return;
  }
  const agentId = state.igEmailSelectedAgentId;
  if (!agentId) {
    return;
  }
  state.igEmailSaving = true;
  state.igEmailError = null;
  const form = state.igEmailForm;
  try {
    const res = await state.client.request<EmailConfigResult>("igloobot.email.updateConfig", {
      agentId,
      smtpHost: form.smtpHost,
      smtpPort: form.smtpPort,
      smtpSecure: form.smtpSecure,
      smtpUser: form.smtpUser,
      smtpPass: form.smtpPass,
      smtpTlsReject: form.smtpTlsReject,
      imapHost: form.imapHost,
      imapPort: form.imapPort,
      imapSecure: form.imapSecure,
      imapUser: form.imapUser,
      imapPass: form.imapPass,
      imapTlsReject: form.imapTlsReject,
      fromAddress: form.fromAddress,
      fromName: form.fromName,
    });
    if (res.ok && res.config) {
      state.igEmailConfig = res.config;
      state.igEmailForm = { ...res.config };
    } else {
      state.igEmailError = res.error ?? "Failed to save email config";
    }
  } catch (err) {
    state.igEmailError = friendlyError(err);
  } finally {
    state.igEmailSaving = false;
  }
}

export async function testEmailConnection(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const agentId = state.igEmailSelectedAgentId;
  if (!agentId) {
    return;
  }
  state.igEmailTesting = true;
  state.igEmailTestResult = null;
  state.igEmailError = null;
  try {
    const res = await state.client.request<EmailTestRpcResult>("igloobot.email.test", {
      agentId,
      testAddress: state.igEmailTestAddress || undefined,
    });
    if (res.ok && res.results) {
      state.igEmailTestResult = res.results;
    } else {
      state.igEmailError = res.error ?? "Test failed";
    }
  } catch (err) {
    state.igEmailError = friendlyError(err);
  } finally {
    state.igEmailTesting = false;
  }
}

export function updateEmailForm(state: IgloobotState, field: string, value: unknown): void {
  if (!state.igEmailForm) return;
  (state.igEmailForm as Record<string, unknown>)[field] = value;
  state.igEmailForm = { ...state.igEmailForm };
}

export async function deleteEmailConfig(state: IgloobotState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const agentId = state.igEmailSelectedAgentId;
  if (!agentId) {
    return;
  }
  state.igEmailSaving = true;
  state.igEmailError = null;
  try {
    const res = await state.client.request<{ ok: boolean; deleted: boolean; error?: string }>(
      "igloobot.email.deleteConfig",
      { agentId },
    );
    if (res.ok) {
      state.igEmailConfig = null;
      state.igEmailForm = { agentId };
      state.igEmailTestResult = null;
      state.igEmailStatus = null;
    } else {
      state.igEmailError = res.error ?? "Failed to delete email config";
    }
  } catch (err) {
    state.igEmailError = friendlyError(err);
  } finally {
    state.igEmailSaving = false;
  }
}

export function selectEmailAgent(state: IgloobotState, agentId: string): void {
  state.igEmailSelectedAgentId = agentId;
  // Clear previous agent's state
  state.igEmailConfig = null;
  state.igEmailForm = null;
  state.igEmailError = null;
  state.igEmailTestResult = null;
  state.igEmailStatus = null;
  // Load config & status for the newly selected agent
  void loadEmailConfig(state);
  void loadEmailStatus(state);
}

/** Build agents list for the email agent picker from the shared agentsList state. */
function buildEmailAgents(state: IgloobotState): EmailAgentOption[] {
  if (!state.agentsList?.agents) {
    return [];
  }
  return state.agentsList.agents.map((a) => ({
    id: a.id,
    name: a.identity?.name ?? a.name ?? a.id,
  }));
}

export function buildEmailProps(state: IgloobotState): EmailProps {
  return {
    loading: state.igEmailLoading,
    error: state.igEmailError,
    saving: state.igEmailSaving,
    config: state.igEmailConfig,
    form: state.igEmailForm,
    testing: state.igEmailTesting,
    testAddress: state.igEmailTestAddress,
    testResult: state.igEmailTestResult,
    status: state.igEmailStatus,
    agents: buildEmailAgents(state),
    selectedAgentId: state.igEmailSelectedAgentId,
    onSelectAgent: (agentId) => selectEmailAgent(state, agentId),
    onRefresh: () => {
      void loadEmailConfig(state);
      void loadEmailStatus(state);
    },
    onSave: () => void saveEmailConfig(state),
    onDelete: () => void deleteEmailConfig(state),
    onTest: () => void testEmailConnection(state),
    onFormChange: (field, value) => updateEmailForm(state, field, value),
    onTestAddressChange: (value) => {
      state.igEmailTestAddress = value;
    },
  };
}

/** Load data for whichever i-GlooBot tab is active */
export function loadIgloobotTab(state: IgloobotState, tab: string): void {
  if (tab === "projects") {
    void loadProjects(state);
    void loadUsers(state);
    // Load agents for the agent picker dropdown in project edit form
    void loadAgents(state as unknown as AgentsState);
  } else if (tab === "tasks") {
    void loadProjects(state);
    void loadTasks(state);
  } else if (tab === "users") {
    void loadProjects(state);
    void loadUsers(state);
  } else if (tab === "mcp-skills") {
    void loadProjects(state);
    void loadMcpSkills(state);
  } else if (tab === "avatar") {
    void loadAvatarStatus(state);
    void loadAvatarConfig(state);
    void loadAvatarSessions(state);
  } else if (tab === "company") {
    void loadCompanies(state);
    void loadCompany(state);
  } else if (tab === "email") {
    // Load agents list first, then auto-select an agent and load its config
    void loadAgents(state as unknown as AgentsState).then(() => {
      state.igEmailAgents = buildEmailAgents(state);
      // Auto-select the first agent if none is selected
      if (!state.igEmailSelectedAgentId && state.igEmailAgents.length > 0) {
        state.igEmailSelectedAgentId = state.igEmailAgents[0].id;
      }
      void loadEmailConfig(state);
      void loadEmailStatus(state);
    });
  }
}

// --------------- Pinned Projects Session Cache ---------------

/** Minimal state shape needed for pin/unpin (settings + applySettings). */
export interface PinState extends IgloobotState {
  settings: UiSettings;
  pinnedProjectSessions: Record<string, ProjectSessionEntry[]>;
  pinnedProjectsExpanded: Record<string, boolean>;
  pinnedProjectSessionsLoading: Record<string, boolean>;
  applySettings(next: UiSettings): void;
}

export async function loadPinnedProjectSessions(
  state: PinState,
  projectId: string,
): Promise<void> {
  if (!state.client || !state.connected) return;
  state.pinnedProjectSessionsLoading = { ...state.pinnedProjectSessionsLoading, [projectId]: true };
  try {
    const r = await state.client.request<{ ok: boolean; sessions?: ProjectSessionEntry[] }>(
      "igloobot.sessions.list",
      { projectId },
    );
    const sessions = (r?.sessions ?? []).filter(
      (s: ProjectSessionEntry) => !(s.metadata as Record<string, unknown>)?.archived,
    );
    state.pinnedProjectSessions = { ...state.pinnedProjectSessions, [projectId]: sessions };
  } catch {
    /* non-fatal; leave prior cache */
  } finally {
    state.pinnedProjectSessionsLoading = {
      ...state.pinnedProjectSessionsLoading,
      [projectId]: false,
    };
  }
}

export async function renameSession(
  state: PinState,
  projectId: string,
  id: string,
  label: string,
): Promise<void> {
  if (!state.client || !state.connected) return;
  await state.client.request("igloobot.sessions.update", { id, label });
  await loadPinnedProjectSessions(state, projectId);
}

export async function archiveSession(
  state: PinState,
  projectId: string,
  id: string,
): Promise<void> {
  if (!state.client || !state.connected) return;
  await state.client.request("igloobot.sessions.update", { id, archived: true });
  await loadPinnedProjectSessions(state, projectId);
}

export function togglePinnedProject(state: PinState, projectId: string): void {
  const expanded = !state.pinnedProjectsExpanded[projectId];
  state.pinnedProjectsExpanded = { ...state.pinnedProjectsExpanded, [projectId]: expanded };
  if (expanded) {
    // Always reload on expand so re-opening shows fresh sessions
    void loadPinnedProjectSessions(state, projectId);
  }
}

/**
 * Create a new project session via igloobot.sessions.create and refresh the
 * pinned-projects cache for that project. Returns the sessionKey of the new
 * session (to be opened in chat), or null on failure.
 */
export async function createPinnedProjectSession(
  state: PinState,
  projectId: string,
): Promise<string | null> {
  if (!state.client || !state.connected || !projectId) return null;
  const r: any = await state.client.request("igloobot.sessions.create", { projectId });
  const session = r?.session ?? r;
  const sessionKey =
    (session?.metadata?.sessionKey as string | undefined) ?? session?.id ?? null;
  // Refresh the pinned cache for this project so the new session shows
  if (state.settings.pinnedProjectIds.includes(projectId)) {
    await loadPinnedProjectSessions(state, projectId);
  }
  return sessionKey;
}

export function pinProject(state: PinState, id: string): void {
  const next = addPin(state.settings.pinnedProjectIds ?? [], id);
  state.applySettings({ ...state.settings, pinnedProjectIds: next });
}

export function unpinProject(state: PinState, id: string): void {
  const next = removePin(state.settings.pinnedProjectIds ?? [], id);
  state.applySettings({ ...state.settings, pinnedProjectIds: next });
}
